import { useContext, useEffect, useState } from "react";
import { Table, Spin, Empty, Tag } from "antd";
import dayjs from "dayjs";
import AntdTable from "../../../antd/Table/Table";
import { filtrationcontext } from "../../../../context/Filtercontext";
import trans from "../../../../utils";
import { useReportTableColumns } from "../../ReportColumns/reportColumns";
import { useLazyGetShelfMovementDailyDetailsQuery } from "../../../../Redux/service/NewReports/ShelfMovement";
import { useGetDropdownPOIsQuery } from "../../../../Redux/service/POIS/POIs";
import { useGetSkuDropdownQuery } from "../../../../Redux/service/Sku/Sku";
import { state as statesData } from "../../../../utils/CountryState/states";
import { cities as citiesData } from "../../../../utils/CountryState/cities";

// ── Expanded row: fetches submission-level details for a given date/poi/sku ──
const DailyDetailsTable = ({ date, poiId, skuId }) => {
	const [trigger, { data, isFetching }] =
		useLazyGetShelfMovementDailyDetailsQuery();

	useEffect(() => {
		trigger({ date, poiId, skuId });
	}, [date, poiId, skuId, trigger]);

	if (isFetching) {
		return (
			<div style={{ padding: 24, textAlign: "center" }}>
				<Spin size="small" />
			</div>
		);
	}

	const rows = data?.data || [];

	if (!rows.length) {
		return (
			<div style={{ padding: 16 }}>
				<Empty description={trans("No submissions")} />
			</div>
		);
	}

	const columns = [
		{
			title: trans("Submitted By"),
			key: "submittedBy",
			dataIndex: ["submittedBy", "name"],
			width: 160,
			render: (val) => val || "—",
		},
		{
			title: trans("Shelf Qty"),
			key: "shelfQuantity",
			dataIndex: "shelfQuantity",
			width: 100,
			align: "center",
			render: (val) => val ?? "—",
		},
		{
			title: trans("Warehouse Qty"),
			key: "warehouseQuantity",
			dataIndex: "warehouseQuantity",
			width: 130,
			align: "center",
			render: (val) => val ?? "—",
		},
		{
			title: trans("Units / Container"),
			key: "unitsPerContainer",
			dataIndex: "unitsPerContainer",
			width: 140,
			align: "center",
			render: (val) => val ?? "—",
		},
		{
			title: trans("Total Stock (pcs)"),
			key: "totalStockUnits",
			dataIndex: "totalStockUnits",
			width: 140,
			align: "center",
			render: (val) => val ?? "—",
		},
		{
			title: trans("Turnover Rate (%)"),
			key: "stockTurnoverRate",
			dataIndex: "stockTurnoverRate",
			width: 140,
			align: "center",
			render: (val) => (val == null ? "N/A" : `${val}%`),
		},
		{
			title: trans("Price"),
			key: "price",
			dataIndex: "price",
			width: 100,
			align: "center",
			render: (val) => (val != null ? val : "—"),
		},
		{
			title: trans("Promotion Price"),
			key: "promotionPrice",
			dataIndex: "promotionPrice",
			width: 130,
			align: "center",
			render: (val) => (val != null ? val : "—"),
		},
		{
			title: trans("Submitted At"),
			key: "submittedAt",
			dataIndex: "submittedAt",
			width: 170,
			align: "center",
			render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "—"),
		},
	];

	return (
		<Table
			columns={columns}
			dataSource={rows}
			rowKey="id"
			size="small"
			pagination={false}
			style={{ margin: "8px 0" }}
		/>
	);
};

// Compares two nullable arrays for equality (prevents spurious filter updates on page/sort-only changes)
const arraysEqual = (a, b) => {
	const na = a ?? null;
	const nb = b ?? null;
	if (na === nb) return true;
	if (!na || !nb) return false;
	if (na.length !== nb.length) return false;
	return na.every((v, i) => String(v) === String(nb[i]));
};

// ── Main table ────────────────────────────────────────────────────────────────
const ShelfMovementReportTable = ({
	data,
	isLoading,
	currentPage,
	setCurrentPage,
	setFilters,
	filters,
}) => {
	console.log("Filters", filters);
	const { data: PoisData } = useGetDropdownPOIsQuery();
	const { data: SkuData } = useGetSkuDropdownQuery();
	const {
		setSort: setSortFilter,
		limit,
		setLimit,
	} = useContext(filtrationcontext);

	useEffect(() => {
		return () => {
			setSortFilter({ by: "", order: "" });
		};
	}, [setSortFilter]);

	const clientOptions = (PoisData?.data || PoisData || []).map((p) => ({
		text: p.name,
		value: p.id,
	}));
	const itemOptions = (SkuData?.data || SkuData || []).map((s) => ({
		text: s.skuName,
		value: s.id,
	}));

	const stateOptions =
		statesData
			.find((i) => i.countryName === "Saudi Arabia")
			?.states?.map((i) => ({
				text: trans(i.name),
				value: i.name,
			})) || [];

	const filterClientPoiIds = filters?.poiIds || null;
	const filterItemSkuIds = filters?.skuIds || null;

	const filterStateSingle = filters?.state || null;
	const filterStateValue = filterStateSingle ? [filterStateSingle] : null;
	const filterCityValue = filters?.tableCities || null;

	const cityOptions = filterStateSingle
		? citiesData
				?.flatMap((i) => i.states || [])
				?.find((s) => s.name === filterStateSingle)
				?.cities?.map((city) => ({
					text: trans(city.name),
					value: city.name,
				})) || []
		: [];

	const [activeFilterKey, setActiveFilterKey] = useState(null);

	const sortKeyMap = {
		date: "date",
		client: "client",
		city: "city",
		state: "state",
		item: "item",
		priceBefore: "priceBefore",
		priceAfter: "priceAfter",
		stockTurnoverRate: "stockTurnoverRate",
		totalSubmissions: "totalSubmissions",
	};

	const handleTableChange = (pagination, tableFilters, sorter) => {
		if (sorter?.order) {
			setSortFilter({
				by: sortKeyMap[sorter.columnKey] || sorter.columnKey,
				order: sorter.order === "ascend" ? "asc" : "desc",
			});
		} else {
			setSortFilter({ by: "", order: "" });
		}

		const newClientPoiIds =
			activeFilterKey === "client"
				? tableFilters?.client || null
				: filterClientPoiIds;
		const newItemSkuIds =
			activeFilterKey === "item"
				? tableFilters?.item || null
				: filterItemSkuIds;
		const newStateSingle =
			activeFilterKey === "state"
				? tableFilters?.state?.[0] || null
				: filterStateSingle;

		const stateJustChanged = newStateSingle !== filterStateSingle;
		const newCityValue = stateJustChanged
			? null
			: activeFilterKey === "city"
				? tableFilters?.city || null
				: filterCityValue;

		const filterChanged =
			!arraysEqual(newClientPoiIds, filterClientPoiIds) ||
			!arraysEqual(newItemSkuIds, filterItemSkuIds) ||
			newStateSingle !== filterStateSingle ||
			!arraysEqual(newCityValue, filterCityValue);

		if (filterChanged) {
			setFilters((prev) => ({
				...prev,
				poiIds: newClientPoiIds,
				skuIds: newItemSkuIds,
				state: newStateSingle,
				tableCities: newCityValue,
			}));
			setCurrentPage(1);
		}
	};

	const columns = [
		{
			title: trans("Client"),
			key: "client",
			dataIndex: "client",
			width: 220,
			sorter: true,
			filters: clientOptions,
			filterSearch: true,
			filterMultiple: true,
			filteredValue: filterClientPoiIds,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("client");
			},
			render: (val) => (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 8,
						overflow: "hidden",
					}}
				>
					<div
						style={{
							width: 4,
							minHeight: 28,
							borderRadius: 2,
							backgroundColor: "#1890ff",
							flexShrink: 0,
						}}
					/>
					<span style={{ fontWeight: 600 }}>{val}</span>
				</div>
			),
		},
		{
			title: trans("Date"),
			key: "date",
			dataIndex: "date",
			width: 120,
			sorter: true,
			render: (val) => (val ? dayjs(val).format("YYYY-MM-DD") : "—"),
		},
		{
			title: trans("State"),
			key: "state",
			dataIndex: "state",
			width: 120,
			sorter: true,
			filters: stateOptions,
			filterSearch: true,
			filterMultiple: false,
			filteredValue: filterStateValue,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("state");
			},
			render: (val) =>
				val ? (
					<Tag color="geekblue" style={{ borderRadius: 10, fontWeight: 500 }}>
						{trans(val)}
					</Tag>
				) : (
					<span style={{ color: "#bbb" }}>—</span>
				),
		},
		{
			title: trans("City"),
			key: "city",
			dataIndex: "city",
			width: 120,
			sorter: true,
			filters: cityOptions,
			filterSearch: true,
			filterMultiple: true,
			filteredValue: filterCityValue,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("city");
			},
			render: (val) =>
				val ? (
					<Tag color="cyan" style={{ borderRadius: 10, fontWeight: 500 }}>
						{trans(val)}
					</Tag>
				) : (
					<span style={{ color: "#bbb" }}>—</span>
				),
		},
		{
			title: trans("ItemShelf"),
			key: "item",
			dataIndex: "item",
			width: 220,
			sorter: true,
			filters: itemOptions,
			filterSearch: true,
			filterMultiple: true,
			filteredValue: filterItemSkuIds,
			onFilter: () => true,
			onFilterDropdownOpenChange: (open) => {
				if (open) setActiveFilterKey("item");
			},
			render: (item) =>
				item ? (
					<span>
						<strong>{item.name}</strong>
						<br />
						<span style={{ color: "#8c8c8c", fontSize: 12 }}>{item.code}</span>
					</span>
				) : (
					"—"
				),
		},
		{
			title: trans("Shelf Qty"),
			key: "shelfQuantityFirst",
			dataIndex: "shelfQuantityFirst",
			width: 110,
			align: "center",
			render: (val) => val ?? "—",
		},
		{
			title: trans("Warehouse Qty"),
			key: "warehouseQuantityFirst",
			dataIndex: "warehouseQuantityFirst",
			width: 140,
			align: "center",
			render: (val) => val ?? "—",
		},
		{
			title: trans("Units / Container"),
			key: "unitsPerContainer",
			dataIndex: "unitsPerContainer",
			width: 140,
			align: "center",
			render: (val) => val ?? "—",
		},
		{
			title: trans("Opening Stock (pcs)"),
			key: "openingStockUnits",
			dataIndex: "openingStockUnits",
			width: 150,
			align: "center",
			render: (val) => val ?? "—",
		},
		{
			title: trans("Closing Stock (pcs)"),
			key: "closingStockUnits",
			dataIndex: "closingStockUnits",
			width: 150,
			align: "center",
			render: (val) => val ?? "—",
		},
		{
			title: trans("Turnover Rate (%)"),
			key: "stockTurnoverRate",
			dataIndex: "stockTurnoverRate",
			width: 140,
			align: "center",
			sorter: true,
			render: (val) => (val == null ? "N/A" : `${val}%`),
		},
		{
			title: trans("Price Before"),
			key: "priceBefore",
			dataIndex: "priceBefore",
			width: 120,
			align: "center",
			sorter: true,
			render: (val) => (val != null ? val : "—"),
		},
		{
			title: trans("Price After"),
			key: "priceAfter",
			dataIndex: "priceAfter",
			width: 120,
			align: "center",
			sorter: true,
			render: (val) => (val != null ? val : "—"),
		},
		{
			title: trans("First Submitted"),
			key: "submittedAtFirst",
			dataIndex: "submittedAtFirst",
			width: 160,
			align: "center",
			render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "—"),
		},
		{
			title: trans("Last Submitted"),
			key: "submittedAtLast",
			dataIndex: "submittedAtLast",
			width: 160,
			align: "center",
			render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "—"),
		},
		{
			title: trans("Total Submissions"),
			key: "totalSubmissions",
			dataIndex: "totalSubmissions",
			width: 140,
			align: "center",
			sorter: true,
			render: (val) => (
				<span
					style={{
						display: "inline-block",
						minWidth: 32,
						padding: "2px 12px",
						borderRadius: 999,
						backgroundColor: "#e6f7ff",
						color: "#1890ff",
						fontWeight: 700,
						border: "1px solid #91d5ff",
					}}
				>
					{val}
				</span>
			),
		},
	];
	const visibleColumns = useReportTableColumns(columns);

	return (
		<div
			style={{
				background: "#fff",
				borderRadius: 10,
				boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
				overflow: "hidden",
				marginTop: 12,
			}}
		>
			<AntdTable
				columns={visibleColumns}
				dataSource={(data?.data || []).map((row) => ({
					...row,
					id: `${row._meta?.poiId}|${row._meta?.skuId}|${row.date}`,
				}))}
				loading={isLoading}
				pageSize={limit}
				totalItems={data?.totalResults || 0}
				currentPage={data?.paginationInfo?.currPage || currentPage}
				onPageChange={(page) => setCurrentPage(page)}
				onLimitChange={(newLimit) => {
					setLimit(newLimit);
					setCurrentPage(1);
				}}
				onTableChange={handleTableChange}
				expandable={{
					expandedRowRender: (record) => (
						<DailyDetailsTable
							date={record.date}
							poiId={record._meta?.poiId}
							skuId={record._meta?.skuId}
						/>
					),
				}}
				scrollY={380}
				scrollX
			/>
		</div>
	);
};

export default ShelfMovementReportTable;
